import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { SESSION_ID } from 'src/app/constants/authentication.constants';
import { CLIENT_CASE_FOLDER_NAME } from 'src/app/constants/folder.constants';
import { splitFilename } from 'src/app/helpers/file-helpers';
import { DocumentModel } from 'src/app/models/document.model';
import { FileModel, FolderModel } from 'src/app/models/folder.model';
import { DialogService } from 'src/app/services/dialog.service';
import { FolderService } from 'src/app/services/folder.service';
import { ToastService } from 'src/app/services/toast.service';
import { WindowService } from 'src/app/services/window.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'tx-browse-documents',
  templateUrl: './browse-documents.component.html',
  styleUrls: ['./browse-documents.component.scss'],
})
export class BrowseDocumentsComponent implements OnInit {
  @Input() allowOpen = true;
  @Input() hideAssembledWithoutCaseFolder = false;
  @Input() existingDocument: string | undefined = '';
  @Input() isDirty: boolean = false;
  @Input() folderPath: string = '';
  @Output() theFolder: EventEmitter<FolderModel> = new EventEmitter<FolderModel>();
  @Output() openDocument: EventEmitter<DocumentModel | null> = new EventEmitter();
  @Output() openInNewTab: EventEmitter<DocumentModel | null> = new EventEmitter();
  public clientCaseFolderName = CLIENT_CASE_FOLDER_NAME;

  // need a get and setter for currentFolder
  private _currentFolder: FolderModel;
  public set currentFolder(folder: FolderModel) {
    this._currentFolder = folder;
    this.theFolder.emit(folder);
  }
  get currentFolder(): FolderModel {
    return this._currentFolder;
  }

  public loading: boolean;
  private rootFolder: FolderModel;
  public clientCaseFolder: FolderModel;
  public submitUrl: string;
  public sessionId = SESSION_ID;
  public selectedFile: FileModel | null;
  public selectedFolder: FolderModel | null;
  public filteredFiles: Array<FileModel>;
  public filteredFolders: Array<FolderModel>;
  public searchTerm: string;
  public path: Array<string>;
  private defaultSubmitUrl: string;
  private pdfSubmitUrl: string;
  @ViewChild('openInNewTabForm') openInNewTabForm!: ElementRef;

  constructor(
    private folderService: FolderService,
    private toastService: ToastService,
    private dialogService: DialogService,
    private windowService: WindowService,
    private ref: ChangeDetectorRef
  ) {
    this.loading = false;
    this._currentFolder = { name: '', path: '', files: [], folders: [] };
    this.rootFolder = { name: '', path: '', files: [], folders: [] };
    this.clientCaseFolder = { name: CLIENT_CASE_FOLDER_NAME, path: '', files: [], folders: [] };
    this.defaultSubmitUrl = environment.endpoints.apiUrl + '/document/opendocumentinnewsession';
    this.pdfSubmitUrl = environment.endpoints.apiUrl + '/pdfdocument/opendocumentinnewsession';
    this.submitUrl = this.defaultSubmitUrl;
    this.selectedFile = null;
    this.selectedFolder = null;
    this.filteredFolders = [];
    this.filteredFiles = [];
    this.searchTerm = '';
    this.path = new Array<string>();
  }

  ngOnInit(): void {
    this.checkPreselectedPath();
  }

  onFileSelected(f: FileModel) {
    this.selectedFolder = null;
    this.selectedFile = f;
    // set hidden form values for open in new tab functionality
    this.setFormField('dcmID', this.selectedFile?.dcmID ?? '');
    this.setFormField('Pathname', f.path ?? '');
    this.setFormField('Folder', f.path ?? '');
    this.setFormField('Filename', f.name ?? '');
    this.setFormField('IsAbsolutePath', 'true');

    const htmlSessionId = document.getElementById(SESSION_ID) as HTMLInputElement;
    if (htmlSessionId) {
      htmlSessionId.value = this.windowService.getSessionIdFromWindow() ?? '';
    }
  }

  setFormField(fieldName: string, value: string) {
    const formField = document.getElementById(fieldName) as HTMLInputElement;
    if (formField) {
      formField.value = value ?? '';
    }
  }

  isFileSelected(f: FileModel) {
    const dcmID = f.dcmID ?? '';
    return this.selectedFile?.dcmID === f.dcmID && this.selectedFile?.name === f.name;
  }

  onOpenInNewTabClicked(f: FileModel) {
    this.onFileSelected(f);
    const extension = splitFilename(f.name).extension;
    const isPdf = extension.toLowerCase() === 'pdf';
    this.submitUrl = isPdf ? this.pdfSubmitUrl : this.defaultSubmitUrl;
    this.ref.detectChanges();

    const document: DocumentModel = {
      dcmID: f.dcmID,
      filename: f.name,
      pathname: f.path,
      isActiveDocument: false,
      saveAsPdf: false,
      answersChanged: false,
      openInAnotherSession: false,
      pdfAnswersChanged: false,
      txdocsAnswersChanged: false,
    };

    if (this.allowOpen) {
      if (this.existingDocument || this.isDirty || isPdf) {
        this.openInNewTabForm.nativeElement.submit();
        this.openInNewTab.emit(document);
      } else {
        this.openDocument.emit(document);
      }
    }
  }

  onFolderSelected(f: FolderModel) {
    this.selectedFile = null;

    this.selectedFolder = f;
  }

  addFolder() {
    this.loading = true;
    this.dialogService.prompt({
      title: 'Create New Folder',
      message: 'Enter folder name:',
      confirmButtonText: 'Add',
      rejectButtonText: 'Cancel',
      confirmCallback: (result) => {
        if ((this._currentFolder.path == '' || this._currentFolder.path == '\\') && result == CLIENT_CASE_FOLDER_NAME) {
          this.toastService.show(`Could not add folder: "${CLIENT_CASE_FOLDER_NAME}"  `, {
            classname: 'bg-danger',
            autohide: true,
          });
          this.loading = false;
          return;
        } else if (this._currentFolder.folders?.some((f) => f.name === result)) {
          this.toastService.show(`Could not add folder: "${result}". A folder with that name already exists`, {
            classname: 'bg-danger',
            autohide: true,
          });
          this.loading = false;
          return;
        } else {
          this.folderService.addFolder(this._currentFolder.path + '\\' + result, result).subscribe({
            next: (res) => {
              if (res) {
                var x = {
                  name: res.name,
                  path: res.path,
                };
                this.filteredFolders.push(x);
                this.filteredFolders = this.filteredFolders.sort((a, b) => a.name.localeCompare(b.name));
              }
            },
            error: (err) => {
              this.loading = false;
              this.toastService.show('Could not add folder.', {
                classname: 'bg-danger',
                autohide: true,
              });
            },
            complete: () => {
              this.loading = false;
            },
          });
        }
      },
      rejectCallback: () => {
        this.loading = false;
      },
    });
  }

  onOpenAssembledNonCaseFolderClicked() {
    this.loading = true;
    this.selectedFile = null;
    this.selectedFolder = null;
    const f = { name: CLIENT_CASE_FOLDER_NAME, path: '\\', files: [], folders: [] } as FolderModel;
    this.folderService.getAssembledNonCaseFolder(f).subscribe({
      next: (res) => {
        if (res) {
          let f = res;
          this.currentFolder = res;
          this.filteredFolders = res.folders ?? [];
          this.filteredFiles = res.files ?? [];
          this.searchTerm = '';
          if (res.name != null && res.name !== '') {
            this.path.push(res.name);
          }
        }
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
      },
    });
  }

  onOpenFolderClicked(f: FolderModel) {
    this.loading = true;
    this.selectedFile = null;
    this.selectedFolder = null;
    this.folderService.getFolder(f).subscribe({
      next: (res) => {
        if (res) {
          f = res;
          this.currentFolder = res;
          this.filteredFolders = res.folders ?? [];
          this.filteredFiles = res.files ?? [];
          this.searchTerm = '';
          if (res.name != null && res.name !== '') {
            this.path.push(res.name);
          }
        }
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
      },
    });
  }

  goFolderUp() {
    const parentFolder: FolderModel = {
      name: this.getParentFolderName(),
      path: this.getParentFolderPath(this.currentFolder),
    };

    this.path.splice(this.path.length - 2);
    this.onOpenFolderClicked(parentFolder);
  }

  clearFilter() {
    this.searchTerm = '';
    this.onFilterChange();
  }

  onFilterChange() {
    const search = this.searchTerm.toLowerCase();
    this.filteredFiles = this.currentFolder?.files?.filter((f) => f.name.toLowerCase().includes(search)) ?? [];
    this.filteredFolders = this.currentFolder?.folders?.filter((f) => f.name.toLowerCase().includes(search)) ?? [];
  }

  private getParentFolderPath(folder: FolderModel) {
    if (folder.path === '') {
      return '';
    }

    const lastSlashCharIndex = folder.path.lastIndexOf('\\');
    const parentPath = folder.path.substring(0, lastSlashCharIndex);

    return parentPath;
  }

  private getParentFolderName() {
    return this.path[this.path.length - 2];
  }

  private checkPreselectedPath() {
    const path = this.getDocumentPathAsArray();

    if (path.length > 0) {
      const folder: FolderModel = {
        name: path[path.length - 1],
        path: this.folderPath,
      };

      this.onOpenFolderClicked(folder);
    } else {
      this.onOpenFolderClicked(this.rootFolder);
    }
  }

  private getDocumentPathAsArray() {
    const path = this.folderPath;

    if (path) {
      const pathArray = path.split('\\').filter((e) => e !== '');
      return pathArray;
    }

    return [];
  }
}
